import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Card as CardType } from '@asia/shared';
import { ApiError, rpcErrorCode } from '../../lib/api';
import { onEvent } from '../../lib/ws';
import {
  useCards,
  useCardTemplates,
  useCardTransactions,
  useCardActions,
  useCardIssueStatus,
} from '../../hooks/useCardsApi';
import { useIssueCard } from '../../hooks/useIssueCard';
import { useTopUpCard } from '../../hooks/useTopUpCard';
import { useWallets } from '../../store/wallets';
import { useToast } from '../../store/toast';
import {
  toDisplayCard,
  cardTxToTransaction,
  cardDetailsApi,
  confirmChallengeApi,
  dismissChallengeApi,
  type CardSecrets,
} from '../../lib/cards';
import { cacheReauth, cachedReauth, clearReauth } from '../../lib/reauth-cache';
import { ReauthGate } from './ReauthGate';
import { haptic } from '../../hooks/useTelegram';
import { fmtDateHeading } from '../../lib/format';
import { FigIcon, type FigIconName } from '../FigIcon';
import { CardFace } from './CardFace';
import { TransactionRow } from './TransactionRow';
import { LoadMore } from './LoadMore';
import { TransactionDetailSheet } from './TransactionDetailSheet';
import { IssueCardSheet, CardManageSheet, DeleteCardDialog } from './CardSheets';
import { IssuingOverlay, CongratsOverlay } from './IssueCelebration';
import { TopUpSheet } from './TopUpSheet';
import { CardChallengeSheet } from './CardChallengeSheet';
import cardEmpty from '../../assets/figma/cards/card-empty.png';

/** Turn a server refusal into something the user can act on. */
function mapIssueError(t: (k: string) => string, code: string): string {
  const map: Record<string, string> = {
    insufficient_balance: t('send.err.insufficient'),
    payment_too_small: t('send.err.insufficient'),
    // The fee payment travels the normal send path, so it also has to clear the
    // minimum transfer amount — a card priced below it can never be paid for.
    below_min_amount: t('cards.priceBelowMinimum'),
    payment_not_confirmed: t('cards.paymentPending'),
    payment_invalid: t('cards.paymentInvalid'),
    issue_payment_pending: t('cards.issuePaymentPending'),
    topup_payment_pending: t('cards.topupPaymentPending'),
    topup_in_progress: t('cards.paymentPending'),
    issue_in_progress: t('cards.issueInProgress'),
    payment_already_used: t('cards.paymentSafe'),
    cards_paused: t('cards.unavailable'),
    cards_unavailable: t('cards.unavailable'),
    card_not_available: t('cards.unavailable'),
    card_has_balance: t('cards.hasBalance'),
    card_not_ready: t('cards.detailsNotReady'),
    provider_registration_failed: t('cards.issueFailed'),
    unsupported_country: t('cards.issueFailed'),
    issue_failed: t('cards.issueFailed'),
    email_required: t('cards.emailInvalid'),
    email_in_use: t('cards.emailInUse'),
    register_required: t('cards.registerRequired'),
    provider_apply_failed: t('cards.issueFailed'),
    provider_recharge_failed: t('cards.topUpFailed'),
    below_card_minimum: t('cards.topUpFailed'),
    above_card_maximum: t('cards.topUpFailed'),
    card_closed: t('cards.topUpFailed'),
    no_pending_challenge: t('cards.challengeGone'),
    challenge_failed: t('cards.challengeFailed'),
    card_status_busy: t('cards.statusBusy'),
    provider_unavailable: t('cards.unavailable'),
    transfer_in_progress: t('cards.paymentPending'),
    transfer_preparing: t('send.err.preparing'),
    rpc_unavailable: t('send.err.network'),
    timeout: t('send.err.timeout'),
    ws_timeout: t('send.err.timeout'),
    ws_closed: t('send.err.timeout'),
    signed_transaction_expired: t('send.err.signedExpired'),
  };
  return map[code] ?? t('cards.issueFailed');
}

function Action({
  icon,
  label,
  onClick,
  busy = false,
  disabled = false,
}: {
  icon: FigIconName;
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  const blocked = busy || disabled;
  return (
    <button
      onClick={() => {
        if (blocked) return;
        haptic();
        onClick();
      }}
      disabled={blocked}
      className="flex flex-1 flex-col items-center justify-center gap-[6px] rounded-[20px] bg-card py-2 shadow-card transition-transform active:scale-95 disabled:active:scale-100 disabled:opacity-50"
    >
      {busy ? (
        <span className="size-8 animate-spin rounded-full border-[3px] border-accent/25 border-t-accent" />
      ) : (
        <FigIcon name={icon} size={32} className="text-accent" />
      )}
      <span className="text-[12px] font-medium leading-none tracking-[-0.3px] text-ink">
        {label}
      </span>
    </button>
  );
}

export function CardsScreen() {
  const { t } = useTranslation();
  const cardsQuery = useCards();
  const issueStatusQuery = useCardIssueStatus();
  const apiCards = cardsQuery.data ?? [];
  const cards = apiCards.map(toDisplayCard);
  // Which card is on screen. Declared up here because the confirmation screen
  // returns early, and its handlers still need to know what they're acting on.
  const [active, setActive] = useState(0);
  const activeId = cards[Math.min(active, Math.max(0, cards.length - 1))]?.id ?? '';
  const templates = useCardTemplates().data ?? [];
  const { rename, setStatus, remove } = useCardActions();
  const { issue, stage: issueStage, hasPendingPayment, rememberPaid, clearPaid } = useIssueCard();
  const { topUp, stage: topUpStage } = useTopUpCard();
  const wallet = useWallets((s) => s.wallets)[0];
  const showToast = useToast((s) => s.show);
  const [detailTx, setDetailTx] = useState<ReturnType<typeof cardTxToTransaction> | null>(null);
  const [sheet, setSheet] = useState<'none' | 'issue' | 'manage' | 'delete' | 'topup'>('none');
  // Issuing and deleting spend money or destroy a card, so both ask for the PIN
  // or biometrics first. Holding the pending action here keeps one gate for both.
  const [authFor, setAuthFor] = useState<
    'issue' | 'delete' | 'details' | 'topup' | 'challenge' | 'freeze' | null
  >(null);
  // The top-up the user has already filled in, waiting on their confirmation.
  const [pendingTopUp, setPendingTopUp] = useState<{ amount: string; walletId: string } | null>(null);
  // Which product the user picked in the issue sheet.
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [pendingIssueEmail, setPendingIssueEmail] = useState<string | undefined>(undefined);
  // In-flight / waiting top-up. Snapshot the card face up front so the overlay
  // survives cards refetch, and keep it up until the balance moves (or error).
  const [toppedUp, setToppedUp] = useState<{
    cardId: string;
    amount: string;
    from: number;
    card: CardType;
  } | null>(null);
  const [challengeBusy, setChallengeBusy] = useState(false);
  // A challenge the user dismissed — don't reopen it at them on every render.
  const [dismissedChallenge, setDismissedChallenge] = useState<string | null>(null);
  // The number and CVV are fetched live and kept only in memory, only for the
  // card being looked at, and dropped as soon as it's turned back over.
  // Fetched once per visit, for every card at once — kept in memory only.
  const [secrets, setSecrets] = useState<Record<string, CardSecrets>>({});
  // Which card is currently showing its number. Only ever set for a card whose
  // details we hold, so a card can't turn over onto an empty face.
  const [revealedId, setRevealedId] = useState<string | null>(null);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const revealActive = () => {
    if (!activeId || detailsBusy) return;
    const api = apiCards.find((c) => c.id === activeId);
    if (api?.status === 'pending' || api?.status === 'failed') {
      haptic();
      showToast(t('cards.detailsNotReady'));
      return;
    }
    if (revealedId === activeId) {
      setRevealedId(null); // turn it back over
      return;
    }
    if (secrets[activeId]) {
      setRevealedId(activeId);
      return;
    }
    const recent = cachedReauth();
    if (recent) void runDetails(recent);
    else setAuthFor('details');
  };

  // Issuing runs behind the confirmation: pay the fee, wait for on-chain
  // confirm, order the card, then the congratulations screen. The overlay stays
  // up for the whole round-trip and shows the card the user is buying.
  const [celebrate, setCelebrate] = useState<'none' | 'loading' | 'done'>('none');
  const [issuedCard, setIssuedCard] = useState<CardType | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [issueRetryable, setIssueRetryable] = useState(false);
  // True after cards.issue accepted the intent but the card row is not here yet
  // (provider/webhook). Keeps the overlay on a clear "waiting" step.
  const [waitProvider, setWaitProvider] = useState(false);
  const issueIntentRef = useRef<string | null>(null);
  const dismissedIssueRef = useRef<string | null>(null);
  // Only treat a null issueStatus as "finished" after we have actually seen
  // pending/failed for this attempt — otherwise a fresh issue races the poll.
  const sawDurableIssueRef = useRef(false);
  const issuingPreview: CardType | null = (() => {
    const tpl = templates.find((x) => x.templateId === pendingTemplateId) ?? templates[0];
    if (!tpl) return null;
    return {
      id: 'issuing',
      label: tpl.name,
      brand: /master/i.test(tpl.paymentSystem ?? '') ? 'mastercard' : 'visa',
      last4: '••••',
      balanceUsd: 0,
      status: 'active',
      supportsApplePay: tpl.supportsApplePay,
      supportsGooglePay: tpl.supportsGooglePay,
      theme: 'light',
      expMonth: 0,
      expYear: 0,
    };
  })();

  const runIssue = async (reauthToken?: string): Promise<boolean> => {
    const template = templates.find((tpl) => tpl.templateId === pendingTemplateId) ?? templates[0];
    if (!template || !wallet) {
      showToast(t('cards.unavailable'));
      return true;
    }
    setAuthFor(null);
    countBeforeIssue.current = apiCards.length;
    setIssueError(null);
    setIssueRetryable(false);
    sawDurableIssueRef.current = false;
    setWaitProvider(false);
    setCelebrate('loading');
    try {
      cacheReauth(reauthToken);
      const result = await issue({
        templateId: template.templateId,
        walletId: wallet.id,
        index: wallet.derivationIndex ?? 0,
        address: wallet.address,
        reauthToken,
        email: pendingIssueEmail,
      });
      issueIntentRef.current = result.intentId;
      void issueStatusQuery.refetch();
      // The provider approves the identity before it will issue anything, so a
      // card usually isn't ready yet. The overlay stays up and the socket event
      // that announces the finished card closes it.
      if (result.card) {
        sawDurableIssueRef.current = false;
        setWaitProvider(false);
        setIssuedCard(toDisplayCard(result.card));
        haptic('heavy');
        setCelebrate('done');
        clearPaid();
      } else {
        sawDurableIssueRef.current = true;
        setWaitProvider(true);
      }
    } catch (e) {
      haptic();
      const code = rpcErrorCode(e);
      const message = mapIssueError(t, code);
      showToast(message);
      setWaitProvider(false);
      if (hasPendingPayment()) {
        setIssueError(message);
        // Provider apply / network blips: payment is durable, retry apply only.
        // payment_invalid = stale resume (e.g. treasury env changed) — paidRef
        // is already cleared; still show Retry so the next attempt re-pays.
        setIssueRetryable(
          [
            'payment_not_confirmed',
            'payment_invalid',
            'timeout',
            'ws_timeout',
            'ws_closed',
            'issue_failed',
            'provider_apply_failed',
            'provider_registration_failed',
            'issue_in_progress',
          ].includes(code),
        );
      } else if (code === 'payment_invalid') {
        setIssueError(message);
        setIssueRetryable(true);
      } else {
        setCelebrate('none');
      }
    }
    return true;
  };

  const runDetails = async (reauthToken?: string): Promise<boolean> => {
    if (!activeId) return true;
    // Never hit Banana while the card is still activating — shared merchant limit.
    const api = apiCards.find((c) => c.id === activeId);
    if (api?.status === 'pending' || api?.status === 'failed') {
      setAuthFor(null);
      showToast(t('cards.detailsNotReady'));
      return true;
    }
    const id = activeId;
    setDetailsBusy(true);
    try {
      // One card only — Banana rate-limits get_info; do not prefetch the wallet.
      cacheReauth(reauthToken);
      const info = await cardDetailsApi(id, reauthToken);
      setAuthFor(null);
      setSecrets((prev) => ({ ...prev, [id]: info }));
      setRevealedId(id);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      setAuthFor(null);
      // The remembered confirmation has run out — ask for it properly.
      if (code === 'reauth_required') {
        clearReauth();
        setAuthFor('details');
      } else {
        haptic();
        showToast(e instanceof ApiError ? mapIssueError(t, code) : t('cards.detailsFailed'));
      }
    } finally {
      setDetailsBusy(false);
    }
    return true;
  };

  const runDelete = async (reauthToken?: string): Promise<boolean> => {
    try {
      cacheReauth(reauthToken);
      await remove.mutateAsync({ id: activeId, reauthToken });
      setAuthFor(null);
      setSheet('none');
    } catch (e) {
      setAuthFor(null);
      haptic();
      showToast(e instanceof ApiError ? mapIssueError(t, e.code) : t('cards.deleteFailed'));
    }
    return true;
  };

  // Always go through ReauthGate — do not reuse a cached top-up token, or a
  // mis-tap right after funding would lock the card without a fresh confirm.
  const runFreeze = async (reauthToken?: string): Promise<boolean> => {
    setAuthFor(null);
    try {
      cacheReauth(reauthToken);
      await setStatus.mutateAsync({ id: activeId, action: 'freeze', reauthToken });
    } catch (e) {
      haptic();
      showToast(e instanceof ApiError ? mapIssueError(t, e.code) : t('cards.statusFailed'));
    }
    return true;
  };

  const runTopUp = async (reauthToken?: string): Promise<boolean> => {
    const pending = pendingTopUp;
    setAuthFor(null);
    if (!pending) return true;
    const source = useWallets.getState().wallets.find((w) => w.id === pending.walletId);
    const preview = cards.find((c) => c.id === activeId);
    if (!source || !preview) {
      showToast(t('cards.topUpFailed'));
      return true;
    }
    // Snapshot the balance before anything moves: the webhook can beat us back
    // here, and a "from" read afterwards would already include the top-up.
    const balanceBefore = apiCards.find((c) => c.id === activeId)?.balanceUsd ?? 0;
    // Show the animated card immediately — payment + provider credit can take a
    // while, and a late overlay looks like "nothing happened".
    setToppedUp({
      cardId: activeId,
      amount: `${pending.amount} USDT`,
      from: balanceBefore,
      card: preview,
    });
    setPendingTopUp(null);
    try {
      cacheReauth(reauthToken);
      await topUp({
        cardId: activeId,
        amount: pending.amount,
        walletId: source.id,
        index: source.derivationIndex ?? 0,
        address: source.address,
        reauthToken,
      });
      haptic('heavy');
    } catch (e) {
      haptic();
      setToppedUp(null);
      showToast(e instanceof ApiError ? mapIssueError(t, e.code) : t('cards.topUpFailed'));
    }
    return true;
  };

  const runChallenge = async (reauthToken?: string): Promise<boolean> => {
    setAuthFor(null);
    setChallengeBusy(true);
    try {
      cacheReauth(reauthToken);
      await confirmChallengeApi(activeId, reauthToken);
      haptic('heavy');
      showToast(t('cards.challengeApproved'));
      void cardsQuery.refetch();
    } catch (e) {
      haptic();
      showToast(e instanceof ApiError ? mapIssueError(t, e.code) : t('cards.challengeFailed'));
    } finally {
      setChallengeBusy(false);
    }
    return true;
  };

  const dismissChallenge = async (challengeId: string) => {
    setDismissedChallenge(challengeId);
    setChallengeBusy(true);
    try {
      await dismissChallengeApi(activeId);
      void cardsQuery.refetch();
    } catch {
      /* local dismiss already hides the sheet */
    } finally {
      setChallengeBusy(false);
    }
  };

  const issuedCount = apiCards.length;
  // How many cards there were when this issuance started, so the arrival of the
  // new one is what ends it — not the mere existence of an older card.
  const countBeforeIssue = useRef(0);
  useEffect(() => {
    if (celebrate !== 'loading' || issuedCount <= countBeforeIssue.current) return;
    const newest = apiCards[0];
    if (!newest) return;
    setIssuedCard(toDisplayCard(newest));
    haptic('heavy');
    setCelebrate('done');
    setWaitProvider(false);
    sawDurableIssueRef.current = false;
    clearPaid();
    // apiCards is rebuilt every render; the count is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrate, issuedCount, clearPaid]);

  // Provider failure is asynchronous for some templates. Stop the issuing
  // spinner and keep the paid retry state instead of leaving it up forever.
  useEffect(
    () =>
      onEvent('card_issue_failed', (data) => {
        const event = data as { intentId?: string } | null;
        if (!issueIntentRef.current || event?.intentId !== issueIntentRef.current) return;
        setWaitProvider(false);
        setIssueError(t('cards.issueFailed'));
        // Payment is already bound — reclaim path lets Retry re-apply.
        setIssueRetryable(true);
      }),
    [t],
  );

  // Restore an *in-flight* issue after reconnect / WebView restart. Do NOT
  // reopen a full-screen error for old `failed` rows — that nagged every visit
  // after admin_reset. Quietly remember a reusable payment for a later Retry.
  useEffect(() => {
    const current = issueStatusQuery.data;
    if (!current || current.templateId == null) return;
    if (dismissedIssueRef.current === current.intentId) return;
    if (celebrate === 'done') return;

    if (current.status === 'failed') {
      if (current.paymentTransferId) {
        rememberPaid(current.templateId, current.paymentTransferId);
      }
      return;
    }

    // pending only
    const sameAttempt = issueIntentRef.current === current.intentId;
    if (celebrate !== 'none' && !sameAttempt) return;

    issueIntentRef.current = current.intentId;
    sawDurableIssueRef.current = true;
    setPendingTemplateId(current.templateId);
    if (current.paymentTransferId) {
      rememberPaid(current.templateId, current.paymentTransferId);
    }
    countBeforeIssue.current = apiCards.length;
    const ageMs = Date.now() - Date.parse(current.updatedAt);
    if (current.paymentTransferId && Number.isFinite(ageMs) && ageMs > 30_000) {
      setIssueError(t('cards.issueInProgress'));
      setIssueRetryable(true);
      setWaitProvider(false);
    } else {
      setIssueError(null);
      setIssueRetryable(false);
      setWaitProvider(true);
    }
    setCelebrate('loading');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    issueStatusQuery.data?.intentId,
    issueStatusQuery.data?.status,
    issueStatusQuery.data?.updatedAt,
    celebrate,
    t,
    rememberPaid,
  ]);

  // `updatedAt` does not change while the row stays pending, so the hydrate
  // effect alone never flips a fresh spinner into Retry — arm a timer.
  useEffect(() => {
    const current = issueStatusQuery.data;
    if (celebrate !== 'loading' || issueError) return;
    if (!current || current.status !== 'pending' || !current.paymentTransferId) return;
    const ageMs = Date.now() - Date.parse(current.updatedAt);
    const waitMs = Math.max(0, 30_000 - (Number.isFinite(ageMs) ? ageMs : 0));
    const timer = setTimeout(() => {
      setIssueError(t('cards.issueInProgress'));
      setIssueRetryable(true);
    }, waitMs);
    return () => clearTimeout(timer);
  }, [celebrate, issueError, issueStatusQuery.data, t]);

  // After we have seen a durable pending/failed row, a later null means the
  // intent finished — refetch cards for congrats, or unlock dismiss/retry.
  useEffect(() => {
    if (celebrate !== 'loading' || issueError) return;
    if (!issueStatusQuery.isSuccess || issueStatusQuery.data != null) return;
    if (!sawDurableIssueRef.current) return;
    let cancelled = false;
    void (async () => {
      const refreshed = await cardsQuery.refetch();
      if (cancelled) return;
      const n = refreshed.data?.length ?? 0;
      if (n > countBeforeIssue.current) return;
      sawDurableIssueRef.current = false;
      setIssueError(t('cards.issueFailed'));
      setIssueRetryable(hasPendingPayment());
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrate, issueError, issueStatusQuery.isSuccess, issueStatusQuery.data, t, hasPendingPayment]);

  useEffect(() => {
    setActive(0);
    setRevealedId(null);
    trackRef.current?.scrollTo({ left: 0 });
  }, [issuedCount]);

  // One confirmation covers this visit to the cards screen. Leaving it — to the
  // wallet, or out of the app — forgets it, so coming back asks again.
  useEffect(() => () => clearReauth(), []);

  // The webhook that credits the card also refreshes this list, so a balance
  // above where the top-up started is proof it landed — no polling, no guessing.
  const topUpSettled =
    !!toppedUp && (apiCards.find((c) => c.id === toppedUp.cardId)?.balanceUsd ?? 0) > toppedUp.from;

  useEffect(() => {
    if (!topUpSettled) return;
    haptic('heavy');
    // Let the settle animation play on the list card, then clear.
    const id = window.setTimeout(() => setToppedUp(null), 2200);
    return () => window.clearTimeout(id);
  }, [topUpSettled]);

  // Safety net if the credit push is missed — poll until the balance moves.
  useEffect(() => {
    if (!toppedUp || topUpSettled) return;
    const id = window.setInterval(() => {
      void cardsQuery.refetch();
    }, 2500);
    return () => window.clearInterval(id);
  }, [toppedUp, topUpSettled, cardsQuery]);

  const topUpStatusLabel = (() => {
    if (!toppedUp || topUpSettled) return null;
    if (topUpStage === 'paying') return t('cards.payingForCard');
    if (topUpStage === 'confirming') return t('cards.confirmingPayment');
    return t('cards.toppingUp');
  })();

  const issueOverlayLabel = (() => {
    if (issueError) return undefined;
    if (issueStage === 'paying') return t('cards.payingForCard');
    if (issueStage === 'confirming') return t('cards.confirmingPayment');
    return t('cards.preparing');
  })();

  const celebration = (
    <>
      {celebrate === 'loading' && (
        <IssuingOverlay
          card={issuingPreview}
          animateCard
          error={issueError}
          showPaymentSafe={issueRetryable && hasPendingPayment()}
          onRetry={
            issueError && issueRetryable
              ? () => {
                  setIssueError(null);
                  setWaitProvider(false);
                  setAuthFor('issue');
                }
              : undefined
          }
          onDismiss={() => {
            dismissedIssueRef.current = issueIntentRef.current;
            sawDurableIssueRef.current = false;
            setWaitProvider(false);
            clearPaid();
            setCelebrate('none');
            setIssueError(null);
          }}
          label={issueOverlayLabel}
          hint={
            !issueError &&
            (waitProvider || issueStage === 'issuing' || issueStage === 'idle' || issueStage === 'quoting')
              ? t('cards.waitingCardHint')
              : null
          }
        />
      )}
      {celebrate === 'done' && issuedCard && (
        <CongratsOverlay
          card={issuedCard}
          onDone={() => {
            setCelebrate('none');
            setIssuedCard(null);
            setWaitProvider(false);
          }}
        />
      )}
    </>
  );

  // Horizontal card carousel: the active slide drives the action row / details.
  const onScroll = () => {
    const el = trackRef.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    const step = first.offsetWidth + 8; // slide width + gap-2
    const idx = Math.round(el.scrollLeft / step);
    setActive((prev) => {
      if (prev !== idx) setRevealedId(null);
      return idx;
    });
  };

  const {
    data: cardTxData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useCardTransactions(activeId);
  const cardTxs = (cardTxData ?? []).map(cardTxToTransaction);

  const card = cards[0];

  // Paying for a card, revealing its number or deleting it all ask for the PIN
  // or biometrics first. It sits over the screen instead of replacing it, so the
  // card underneath stays mounted and can still be turned over afterwards.
  const authGate =
    authFor &&
    createPortal(
      <div className="fixed inset-0 z-[90] bg-card">
        <ReauthGate
          onAuthed={
            authFor === 'issue'
              ? runIssue
              : authFor === 'details'
                ? runDetails
                : authFor === 'topup'
                  ? runTopUp
                  : authFor === 'challenge'
                    ? runChallenge
                    : authFor === 'freeze'
                      ? runFreeze
                      : runDelete
          }
          bioTitle={
            authFor === 'issue'
              ? t('cards.confirmIssue')
              : authFor === 'details'
                ? t('cards.confirmDetails')
                : authFor === 'topup'
                  ? t('cards.confirmTopUp')
                  : authFor === 'challenge'
                    ? t('cards.confirmChallenge')
                    : authFor === 'freeze'
                      ? t('cards.confirmFreeze')
                      : t('cards.confirmDelete')
          }
          bioHint={t('security.sendUnlockHint')}
        />
      </div>,
      document.body,
    );

  // Until the cards have actually loaded, "no cards" isn't true yet — showing the
  // empty state here made an existing card look absent for a moment.
  if (cardsQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        {celebration}
        {authGate}
      </div>
    );
  }

  // ---- empty state (Figma 2:6079; vertically centered per product feedback;
  // illustration area is 402×275 with the dashed frame and the tilted card
  // centered — sized in % so it scales with the viewport) ----
  if (!card) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center pb-40">
        <div className="relative aspect-[402/275] w-full max-w-[402px]">
          <div className="card-empty-frame absolute left-1/2 top-[13.34%] h-[73.34%] w-[77.11%] rounded-[33.614px] border-4 border-dashed border-ink/80" />
          <span className="absolute left-1/2 top-[0.12%] w-[87.47%] -translate-x-1/2">
            <img src={cardEmpty} alt="" className="card-empty-float block w-full" />
          </span>
        </div>

        <div className="mt-6 flex flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-[18px] font-medium text-ink">{t('cards.emptyTitle')}</p>
            <p className="w-[300px] text-[14px] leading-[1.44] text-muted">
              {t('cards.emptyDesc')}
            </p>
          </div>
          <button
            onClick={() => {
              haptic();
              setSheet('issue');
            }}
            className="flex h-[52px] w-[181px] items-center justify-center rounded-full border border-card bg-accent text-[15px] font-bold text-white active:opacity-90"
          >
            {t('cards.issueShort')}
          </button>
        </div>

        {sheet === 'issue' && (
          <IssueCardSheet
            templates={templates}
            onClose={() => setSheet('none')}
            onIssue={(templateId, email) => {
              setSheet('none');
              setPendingTemplateId(templateId);
              setPendingIssueEmail(email);
              setAuthFor('issue');
            }}
          />
        )}
        {celebration}
        {authGate}
      </div>
    );
  }

  // ---- card state ----
  const onAddTile = active >= cards.length;
  const activeCard = cards[Math.min(active, cards.length - 1)] ?? card;
  // A purchase or wallet-provisioning check waiting on this card's owner. It
  // appears on its own — the provider is holding the transaction until it's
  // approved, so it shouldn't wait behind a menu.
  const activeApiCard = apiCards.find((c) => c.id === activeCard.id) ?? null;
  const cardActivating =
    activeApiCard?.status === 'pending' || activeApiCard?.status === 'failed';
  const activeTemplate =
    templates.find((tpl) => tpl.templateId === activeApiCard?.templateId) ?? null;
  const pendingChallenge = activeApiCard?.pendingChallenge ?? null;
  const activeChallenge =
    pendingChallenge && pendingChallenge.id !== dismissedChallenge && !authFor
      ? pendingChallenge
      : null;

  const byDay = cardTxs.reduce<Record<string, typeof cardTxs>>((acc, tx) => {
    (acc[fmtDateHeading(tx.date)] ??= []).push(tx);
    return acc;
  }, {});

  return (
    <div className="min-h-full px-4 pb-40 pt-3">
      {/* Horizontal card slider — Figma 2:5860: each slide is the full content
          width (370/402), gap 8px, so the next card shows only an 8px sliver
          inside the right screen padding. Snap-scrolls between slides. */}
      {/* py-12/-my-12: an overflow-x container also clips vertically, which was
          slicing the card's drop shadow into a hard horizontal edge — the padding
          gives the shadow room inside the clip, the negative margin cancels it. */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="no-scrollbar -mx-4 -my-12 flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-px-4 px-4 py-12"
      >
        {cards.map((c) => {
          const shown = secrets[c.id] ?? null;
          const [mm, yy] = (shown?.expiry ?? '').split('/');
          const face = shown
            ? {
                ...c,
                number: shown.number,
                cvv: shown.cvv,
                expMonth: Number(mm) || c.expMonth,
                expYear: Number(yy) || c.expYear,
              }
            : c;
          const isTopUpTarget = toppedUp?.cardId === c.id;
          const creditState = isTopUpTarget
            ? topUpSettled
              ? 'done'
              : 'pending'
            : 'idle';
          return (
            <div key={c.id} className="w-full shrink-0 snap-start">
              <CardFace
                card={face}
                flippable
                onReveal={revealActive}
                revealed={revealedId === c.id}
                creditState={creditState}
                creditHint={
                  isTopUpTarget && !topUpSettled ? `+${toppedUp.amount}` : null
                }
              />
            </div>
          );
        })}
        {/* Add-card tile — always the last slide */}
        <button
          onClick={() => {
            haptic();
            setSheet('issue');
          }}
          className="flex aspect-[370/216] w-full shrink-0 snap-start flex-col items-center justify-center gap-2 rounded-[36px] border-2 border-dashed border-border active:bg-press/[0.03]"
        >
          <FigIcon name="add" size={34} className="text-accent" />
          <span className="text-[15px] font-medium text-ink">{t('cards.addCard')}</span>
        </button>
      </div>

      {/* Below the card: actions + this card's transactions. Hidden entirely on
          the Add-card slide — there's no card selected yet, so nothing to show. */}
      {!onAddTile && (
        <>
          {topUpStatusLabel ? (
            <p className="mt-1 text-center text-[13px] font-medium tracking-[-0.2px] text-accent">
              {topUpStatusLabel}
            </p>
          ) : topUpSettled ? (
            <p className="mt-1 text-center text-[13px] font-medium tracking-[-0.2px] text-positive">
              {t('cards.toppedUpDesc', { amount: toppedUp!.amount })}
            </p>
          ) : cardActivating ? (
            <p className="mt-1 text-center text-[13px] font-medium tracking-[-0.2px] text-muted">
              {t('cards.pendingHint')}
            </p>
          ) : null}
          <div className="mt-4 flex gap-2">
            <Action
              icon="receive"
              label={t('cards.topUp')}
              onClick={() => {
                if (cardActivating) {
                  haptic();
                  showToast(t('cards.detailsNotReady'));
                  return;
                }
                setSheet('topup');
              }}
              busy={!!toppedUp && !topUpSettled}
              disabled={cardActivating}
            />
            <Action
              icon="pfCard"
              label={t('cards.details')}
              onClick={revealActive}
              busy={detailsBusy}
              disabled={cardActivating}
            />
            <Action
              icon="pfSun"
              label={activeCard.status === 'frozen' ? t('cards.unfreeze') : t('cards.freeze')}
              busy={setStatus.isPending}
              disabled={cardActivating || (!!toppedUp && !topUpSettled)}
              onClick={() => {
                if (cardActivating) {
                  haptic();
                  showToast(t('cards.detailsNotReady'));
                  return;
                }
                if (activeCard.status === 'frozen') {
                  setStatus.mutate(
                    { id: activeCard.id, action: 'unfreeze' },
                    {
                      onError: (e) => {
                        haptic();
                        showToast(
                          e instanceof ApiError
                            ? mapIssueError(t, e.code)
                            : t('cards.statusFailed'),
                        );
                      },
                    },
                  );
                  return;
                }
                setAuthFor('freeze');
              }}
            />
            <Action
              icon="pfSetting"
              label={t('cards.settingsShort')}
              onClick={() => setSheet('manage')}
            />
          </div>

          {cardTxs.length === 0 ? (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-card bg-card px-6 py-10 shadow-card">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface">
                <FigIcon name="pfReceipt" size={26} className="text-muted" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-[15px] font-semibold text-ink">{t('cards.noTx')}</p>
                <p className="max-w-[240px] text-center text-[13px] leading-[1.4] text-muted">
                  {t('cards.noTxHint')}
                </p>
              </div>
            </div>
          ) : (
            <>
              {Object.entries(byDay).map(([day, txs]) => (
                <section key={day} className="mt-6">
                  <h3 className="px-1 text-[15px] font-semibold text-muted">{day}</h3>
                  <div className="mt-2 divide-y divide-border overflow-hidden rounded-card bg-card shadow-card">
                    {txs.map((tx) => (
                      <TransactionRow
                        key={tx.id}
                        tx={tx}
                        badge={tx.merchant ?? null}
                        onClick={() => setDetailTx(tx)}
                      />
                    ))}
                  </div>
                </section>
              ))}
              <LoadMore hasMore={!!hasNextPage} loading={isFetchingNextPage} onLoadMore={fetchNextPage} />
            </>
          )}
        </>
      )}

      {detailTx && <TransactionDetailSheet tx={detailTx} onClose={() => setDetailTx(null)} />}

      {sheet === 'issue' && (
        <IssueCardSheet
          templates={templates}
          onClose={() => setSheet('none')}
          onIssue={(templateId, email) => {
              setSheet('none');
              setPendingTemplateId(templateId);
              setPendingIssueEmail(email);
              setAuthFor('issue');
            }}
        />
      )}
      {sheet === 'topup' && (
        <TopUpSheet
          card={activeCard}
          onClose={() => setSheet('none')}
          onConfirm={(amount, walletId) => {
            setSheet('none');
            setPendingTopUp({ amount, walletId });
            setAuthFor('topup');
          }}
        />
      )}
      {activeChallenge && (
        <CardChallengeSheet
          challenge={activeChallenge}
          busy={challengeBusy}
          onApprove={() => setAuthFor('challenge')}
          onDismiss={() => void dismissChallenge(activeChallenge.id)}
        />
      )}
      {sheet === 'manage' && (
        <CardManageSheet
          card={activeCard}
          template={activeTemplate}
          onClose={() => setSheet('none')}
          onRename={(label) => rename.mutate({ id: activeCard.id, label })}
          onDelete={() => setSheet('delete')}
        />
      )}
      {celebration}
      {authGate}
      {sheet === 'delete' && (
        <DeleteCardDialog
          card={activeCard}
          onCancel={() => setSheet('none')}
          onConfirm={() => setAuthFor('delete')}
        />
      )}
    </div>
  );
}
